import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import {
  aplicarTema,
  lerTemaSalvo,
  salvarTema,
  type Tema,
  ThemeContext,
  type ThemeContextValue,
} from './theme-context'

export interface ThemeProviderProps {
  readonly children: ReactNode
  /** Tema inicial forçado. Usado em teste; em produção vem do localStorage. */
  readonly temaInicial?: Tema
}

export function ThemeProvider({ children, temaInicial }: ThemeProviderProps): ReactElement {
  const [tema, setTema] = useState<Tema>(() => temaInicial ?? lerTemaSalvo())

  // A classe entra no <html> antes da pintura para não piscar o tema errado.
  useEffect(() => {
    aplicarTema(tema)
  }, [tema])

  const definirTema = useCallback((proximo: Tema) => {
    setTema(proximo)
    salvarTema(proximo)
  }, [])

  const alternarTema = useCallback(() => {
    setTema((atual) => {
      const proximo: Tema = atual === 'dark' ? 'light' : 'dark'
      salvarTema(proximo)
      return proximo
    })
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ tema, definirTema, alternarTema }),
    [tema, definirTema, alternarTema],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
